/**
 * Risoluzione di URL di immagini servite dall'ISTANZA Liferay corretta (ritratto
 * utente, logo azienda, ...).
 *
 * Il backend valorizza questi URL con path RELATIVI ("/image/...?img_id=...").
 * Caricati da un `<img>` su una SPA con origin diversa (dev, o build separata)
 * punterebbero all'origin della SPA -> 404. Vanno resi assoluti verso Liferay.
 *
 * MULTI-TENANT: istanze diverse hanno virtual host diversi, quindi NON basta
 * l'origin di `CRM_BASE_URL`. Replichiamo la logica di `httpClient.buildUrl`:
 * schema/porta da `CRM_BASE_URL`, ma hostname riscritto con `getApiHost()` (il
 * virtual host della company dell'utente, ricevuto al login). L'`<img>` non passa
 * dall'interceptor del client, quindi la riscrittura va fatta qui.
 */

import { CRM_BASE_URL } from './config';
import { getApiHost } from '../auth/tokenManager';

function rewriteHost(absUrl: string, host: string): string {
  try {
    const parsed = new URL(absUrl);
    parsed.hostname = host;
    return parsed.toString();
  } catch {
    return absUrl;
  }
}

/**
 * Risolve un URL immagine Liferay (relativo o assoluto) in URL assoluto verso
 * l'istanza dell'utente. Vuoto/assente -> `undefined`. Se `CRM_BASE_URL` è
 * relativo (SPA servita da Liferay, stessa origin) l'URL relativo è già corretto
 * e viene lasciato com'è. Usato sia per il ritratto utente sia per il logo azienda.
 */
export function resolveLiferayImageURL(url: string | null | undefined): string | undefined {
  const u = url?.trim();
  if (!u) return undefined;

  // Data URL (es. anteprima locale o mock che echeggia l'upload): già autonomo.
  if (u.startsWith('data:')) return u;

  const apiHost = getApiHost();

  // Già assoluto: eventuale riscrittura dell'host per il multi-tenant.
  if (/^https?:\/\//i.test(u)) return apiHost ? rewriteHost(u, apiHost) : u;

  // Relativo + CRM_BASE_URL assoluto: prefissa l'origin (host = apiHost se noto).
  if (/^https?:\/\//i.test(CRM_BASE_URL)) {
    try {
      const base = new URL(CRM_BASE_URL);
      if (apiHost) base.hostname = apiHost;
      return `${base.origin}${u.startsWith('/') ? '' : '/'}${u}`;
    } catch {
      return u;
    }
  }

  // CRM_BASE_URL relativo: stessa origin della SPA (= Liferay) -> lascia relativo.
  return u;
}

/**
 * Alias per il ritratto utente: è un'immagine Liferay come le altre, ma il nome
 * documenta l'uso al call site (avatar). Stessa implementazione condivisa.
 */
export const resolvePortraitURL = resolveLiferayImageURL;
